/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Directive, ElementRef, Input, ChangeDetectorRef, Renderer2, NgZone } from '@angular/core';
import { TextService, ChangedEvent } from '@typescript-kit/core';
import { BaseDirective } from './base.directive';
import { TextModel } from '@typescript-kit/view';

@Directive({
  selector: '[kitText]'
})
export class TextDirective extends BaseDirective<TextModel> {
  private _text: string;
  private _textArgs: object;
  private _alignment: string;

  constructor(
    changeDetector: ChangeDetectorRef,
    elementRef: ElementRef,
    renderer: Renderer2,
    ngZone: NgZone,
    textService: TextService
  ) {
    super(changeDetector, elementRef, renderer, ngZone, textService);
  }

  get hostClass(): string {
    return 'kit-text';
  }

  @Input('kitText')
  set text(value: TextModel | string) {
    if (!value) {
      // empty model is not allowed, but may be called by empty component selector 'kitModel'
      // in this case model must be set via model property (e. g., by kit component directive)
      // empty string must be set via model.text property
      return;
    }
    if (typeof value === 'string') {
      this._text = value;
      this.model = new TextModel({ text: this._text, textArgs: this._textArgs });
    } else {
      this._text = undefined;
      this.model = value;
    }
  }

  get text(): TextModel | string {
    return this._text !== undefined ? this._text : this.model;
  }

  @Input('kitTextArgs')
  set textArgs(value: object) {
    this._textArgs = value;
    if (this.model) {
      this.model.textArgs = value;
    }
  }

  get textArgs(): object {
    return this._textArgs;
  }

  protected onModelChanged(model: TextModel) {
    super.onModelChanged(model);
    this.refreshText();
    this.refreshAlignment();
  }

  protected onModelPropertyChanged(event: ChangedEvent): void {
    super.onModelPropertyChanged(event);
    if (event.changes['text'] || event.changes['textArgs']) {
      this.refreshText();
    }
    if (event.changes['alignment']) {
      this.refreshAlignment();
    }
  }

  private refreshText() {
    this.element.innerText = this.textService.getText(this.model.text, this.model.textArgs);
  }

  private refreshAlignment() {
    if (this._alignment) {
      this.renderer.removeClass(this.element, this._alignment);
      this._alignment = null;
    }
    if (this.model && this.model.alignment) {
      this._alignment = `kit-alignment-${this.model.alignment}`;
      this.renderer.addClass(this.element, this._alignment);
    }
  }

}
